import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * 根据时间戳和用户ID创建唯一标识符
 * @param timestamp 时间戳（毫秒）
 * @param userId 用户ID
 * @returns 生成的唯一标识符字符串
 *
 * 示例：
 * - generateUUIDFromTimestamp(1704067200000, 'user123') => 'a1b2c3d4e5f6'
 */
export function generateUUIDFromTimestamp(
  timestamp: number,
  userId: string
): string {
  // 将时间戳转换为16进制字符串（取前8位）
  const timestampHex = timestamp.toString(16).padStart(8, '0').slice(0, 8)

  // 使用用户ID生成一个哈希值（取前4位）
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // 转换为32位整数
  }
  const hashHex = Math.abs(hash).toString(16).padStart(4, '0').slice(0, 4)

  // 生成随机部分（4位）
  const random = Math.floor(Math.random() * 0x10000)
    .toString(16)
    .padStart(4, '0')

  // 组装：时间戳8位 + 用户哈希4位 + 随机4位 = 16位
  return timestampHex + hashHex + random
}

// 主题色系列表
const themeColors = [
  { bg: '#EFF7FF', text: '#1E53FF' }, // 蓝色系
  { bg: '#F3EFFF', text: '#8752FF' }, // 紫色系
  { bg: '#FFF3EF', text: '#FF5255' }, // 红色系
  { bg: '#EFFFE7', text: '#52FF8B' }, // 绿色系
  { bg: '#FFE7EF', text: '#FF52A1' }, // 粉色系
]

/**
 * 根据用户名生成一致的颜色
 * @param name 用户名
 * @returns 背景色和文字颜色
 */
export function generateAvatarColors(name: string) {
  // 使用用户名的字符编码之和作为索引，确保同一用户名总是得到相同的颜色
  const sum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const index = sum % themeColors.length
  return themeColors[index]
}

/**
 * 获取用户名的首字母（支持中文）
 * @param name 用户名
 * @returns 首字母或首个字
 */
export function getNameInitial(name: string) {
  if (!name) return ''
  return name.charAt(0).toUpperCase()
}

/**
 * 格式化时间为指定格式
 * @param dateString 日期字符串
 * @param format 格式化字符串，默认为 'yyyy-MM-dd HH:mm'
 * @returns 格式化后的时间字符串
 *
 * 支持的格式占位符：
 * - yyyy: 4位年份
 * - MM: 2位月份（01-12）
 * - dd: 2位日期（01-31）
 * - HH: 2位小时（00-23）
 * - mm: 2位分钟（00-59）
 * - ss: 2位秒数（00-59）
 * - SSS: 3位毫秒（000-999）
 *
 * 示例：
 * - formatDateTime('2024-06-27T10:30:00Z') => '2024-06-27 10:30'
 * - formatDateTime('2024-06-27T10:30:00Z', 'yyyy/MM/dd') => '2024/06/27'
 * - formatDateTime('2024-06-27T10:30:00Z', 'HH:mm:ss') => '10:30:00'
 */
export const formatDateTime = (
  dateString: string | number,
  format: string = 'yyyy-MM-dd HH:mm'
): string => {
  try {
    const date = new Date(dateString)

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '-'
    }

    // 定义格式化映射
    const formatMap: Record<string, string> = {
      yyyy: String(date.getFullYear()),
      MM: String(date.getMonth() + 1).padStart(2, '0'),
      dd: String(date.getDate()).padStart(2, '0'),
      HH: String(date.getHours()).padStart(2, '0'),
      mm: String(date.getMinutes()).padStart(2, '0'),
      ss: String(date.getSeconds()).padStart(2, '0'),
      SSS: String(date.getMilliseconds()).padStart(3, '0'),
    }

    // 执行格式化替换
    let result = format
    for (const [placeholder, value] of Object.entries(formatMap)) {
      result = result.replace(new RegExp(placeholder, 'g'), value)
    }

    return result
  } catch (error) {
    console.error('时间格式化错误:', error)
    return '-'
  }
}

/**
 * 格式化文件大小
 * @param bytes 文件大小（字节）
 * @returns 格式化后的文件大小字符串
 *
 * 示例：
 * - formatFileSize(1024) => '1 KB'
 * - formatFileSize(1234567) => '1.18 MB'
 * - formatFileSize(1234567890) => '1.15 GB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

/**
 * 下载文件工具函数
 * @param fileData 文件数据（base64字符串或Blob）
 * @param filename 文件名
 * @param mimeType MIME类型
 * @returns Promise<void>
 */
export async function downloadFile(
  fileData: string | Blob,
  filename: string,
  mimeType: string = 'application/octet-stream'
): Promise<void> {
  try {
    let blob: Blob

    if (typeof fileData === 'string') {
      // 如果是base64字符串，需要解码
      if (fileData.startsWith('data:')) {
        // 已经是data URL格式
        const response = await fetch(fileData)
        blob = await response.blob()
      } else {
        // 假设是base64字符串，需要添加前缀
        const base64Data = fileData
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        blob = new Blob([byteArray], { type: mimeType })
      }
    } else {
      // 已经是Blob对象
      blob = fileData
    }

    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'

    // 添加到DOM并触发下载
    document.body.appendChild(link)
    link.click()

    // 清理
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('文件下载失败:', error)
    throw new Error('文件下载失败')
  }
}

/**
 * 下载模板文件
 * @param templateData 模板数据对象
 * @returns Promise<void>
 */
export async function downloadTemplate(templateData: {
  file: string
  filename: string
  description?: string
}): Promise<void> {
  try {
    await downloadFile(
      templateData.file,
      templateData.filename,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    )
  } catch (error) {
    console.error('模板下载失败:', error)
    throw new Error('模板下载失败')
  }
}
